import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ITItem {
  fmRecordId: string
  Hersteller: string | null
  Typ: string | null
  Ort: string | null
  Verantwortung: string | null
  Seriennummer: string | null
  Model: string | null
  Firma: string | null
  kategorie: string | null
  kategorie_derived: string | null
  bemerkung: string | null
  // Purchase + warranty
  einkaufsdatum: string | null
  einkaufspreis: number | null
  einkaufsort: string | null
  garantie_bis: string | null
  garantie_erweitert: boolean
  // Location / condition
  zustand: string | null   // 'neu' | 'gebraucht' | 'defekt' | null
  raum: string | null
  seriennummer_override: string | null
  notiz: string | null
  // Decommission
  entsorgungsdatum: string | null
  entsorgungsgrund: string | null
  is_decommissioned: boolean
  // Computed
  warranty_status: 'valid' | 'soon' | 'expired' | 'expired_assumed' | 'assumed' | 'unknown'
  warranty_days_left: number | null
  marktwert: number | null
  augment_updated_at: string | null
  augment_updated_by: string | null
}

export interface ITAugmentInput {
  kategorie?: string | null
  bemerkung?: string | null
  einkaufsdatum?: string | null
  einkaufspreis?: number | null
  einkaufsort?: string | null
  garantie_bis?: string | null
  garantie_erweitert?: boolean
  zustand?: string | null
  raum?: string | null
  seriennummer_override?: string | null
  notiz?: string | null
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useInventarIT(schuleAbk: string | null, includeDecommissioned = false) {
  return useQuery({
    queryKey: ['inventar-it', schuleAbk, includeDecommissioned],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (schuleAbk) qs.set('schuleAbk', schuleAbk)
      if (includeDecommissioned) qs.set('includeDecommissioned', 'true')
      return api.get<ITItem[]>(`/api/inventar/it?${qs}`)
    },
    enabled: !!schuleAbk,
    staleTime: 5 * 60_000,
  })
}

export function useUpdateITAugment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fmRecordId, data }: { fmRecordId: string; data: ITAugmentInput }) =>
      api.put<{ ok: boolean }>(`/api/inventar/it/${fmRecordId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventar-it'] }),
  })
}

export function useDecommissionIT() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fmRecordId, grund }: { fmRecordId: string; grund?: string }) => {
      const qs = grund ? `?grund=${encodeURIComponent(grund)}` : ''
      return api.delete<{ ok: boolean }>(`/api/inventar/it/${fmRecordId}${qs}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventar-it'] }),
  })
}

export function useRestoreIT() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fmRecordId }: { fmRecordId: string }) =>
      api.post<{ ok: boolean }>(`/api/inventar/it/${fmRecordId}/restore`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventar-it'] }),
  })
}
